export const APP_MEDIA_PREFIX = '/AppMedia/';
export const FILE_STORE_UPLOAD_URL = '/umbraco/Api/FileStore/SaveFile';
export const IMAGE_STORE_UPLOAD_URL = '/umbraco/Api/ImageStore/SaveImage';
export const PENDING_FILE_PREFIX = 'pending-media://';
export const ORIGINAL_MEDIA_SRC_ATTR = 'data-original-src';
export const ORIGINAL_MEDIA_HREF_ATTR = 'data-original-href';
export const RESOLVED_MEDIA_ATTR = 'data-resolved-media-url';

const PRESIGNED_QUERY_PARAM_NAMES = new Set([
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-expires',
  'x-amz-security-token',
  'x-amz-date',
  'x-amz-algorithm',
  'awsaccesskeyid',
  'signature',
  'expires',
]);

export type ResolvedMediaItem = {
  path: string;
  url: string;
};

export type PendingUpload = {
  file: File;
  type: 'image' | 'file';
  objectUrl?: string;
};

export const IMAGE_FILE_EXTENSIONS = /\.(?:avif|bmp|gif|jpe?g|png|svg|webp)$/i;

/** MIME metadata is missing or generic for some clipboard and drag-and-drop files. */
export const isImageFile = (file: Pick<File, 'name' | 'type'>): boolean => {
  const mimeType = file.type.trim().toLowerCase();
  if (mimeType.startsWith('image/')) return true;
  if (mimeType && mimeType !== 'application/octet-stream') return false;

  return IMAGE_FILE_EXTENSIONS.test(file.name.trim());
};

export const normalizeUploadContextId = (value?: string | number): string | null => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!normalized || normalized === '0') {
    return null;
  }
  return normalized;
};

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const extractUploadUrl = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as Record<string, unknown>;
  const rawUrl = data.url ?? data.Url ?? data.default ?? data.Default;
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
    return null;
  }
  return rawUrl.trim();
};

export const isAppMediaPath = (value: string): boolean => value.trim().startsWith(APP_MEDIA_PREFIX);

export const decodeHtmlEntities = (value: string): string => {
  if (!value || !value.includes('&')) {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

export const extractAppMediaPath = (value: string): string | null => {
  if (!value) {
    return null;
  }

  const normalized = decodeHtmlEntities(value.trim());
  if (!normalized) {
    return null;
  }

  if (isAppMediaPath(normalized)) {
    return normalized.split('?')[0].split('#')[0];
  }

  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const parsed = new URL(normalized, origin);
    const path = decodeURIComponent(parsed.pathname || '');

    if (path.startsWith(APP_MEDIA_PREFIX)) {
      return path;
    }

    const appMediaIndex = path.indexOf(APP_MEDIA_PREFIX);
    if (appMediaIndex >= 0) {
      return path.substring(appMediaIndex);
    }
  } catch {
    return null;
  }

  return null;
};

export const hasPresignedUrlParams = (value: string): boolean => {
  if (!value) return false;

  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const parsed = new URL(decodeHtmlEntities(value.trim()), origin);

    for (const key of parsed.searchParams.keys()) {
      if (PRESIGNED_QUERY_PARAM_NAMES.has(key.toLowerCase())) {
        return true;
      }
    }
  } catch {
    return /[?&](x-amz-signature|x-amz-credential|x-amz-expires|awsaccesskeyid|signature|expires)=/i.test(
      value
    );
  }

  return false;
};

const removeResolverAttributes = (element: Element) => {
  element.removeAttribute(ORIGINAL_MEDIA_SRC_ATTR);
  element.removeAttribute(ORIGINAL_MEDIA_HREF_ATTR);
  element.removeAttribute(RESOLVED_MEDIA_ATTR);
};

const shouldInspectMediaHtml = (html: string): boolean =>
  /AppMedia|x-amz-|AWSAccessKeyId|Signature=|Expires=|data-original-src|data-original-href|data-resolved-media-url/i.test(
    html
  );

const restoreMediaAttribute = (
  element: Element,
  attributeName: 'src' | 'href',
  originalAttributeName: typeof ORIGINAL_MEDIA_SRC_ATTR | typeof ORIGINAL_MEDIA_HREF_ATTR
) => {
  const originalPath = extractAppMediaPath(element.getAttribute(originalAttributeName) ?? '');
  const currentValue = element.getAttribute(attributeName) ?? '';
  const currentAppMediaPath = extractAppMediaPath(currentValue);

  if (originalPath) {
    element.setAttribute(attributeName, originalPath);
  } else if (currentAppMediaPath) {
    element.setAttribute(attributeName, currentAppMediaPath);
  } else if (hasPresignedUrlParams(currentValue)) {
    element.removeAttribute(attributeName);
  }

  removeResolverAttributes(element);
};

export const normalizeMediaBeforeSave = (html: string): string => {
  if (!html || !shouldInspectMediaHtml(html) || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('img').forEach((img) => {
    restoreMediaAttribute(img, 'src', ORIGINAL_MEDIA_SRC_ATTR);
  });

  doc.querySelectorAll('a').forEach((anchor) => {
    restoreMediaAttribute(anchor, 'href', ORIGINAL_MEDIA_HREF_ATTR);
  });

  return doc.body.innerHTML;
};

export const extractResolvedMediaItems = (payload: unknown): ResolvedMediaItem[] => {
  if (!payload || typeof payload !== 'object') return [];

  const root = payload as Record<string, unknown>;
  const unwrapped = (root.Data ?? root.data ?? root) as unknown;

  const normalizeItem = (item: unknown): ResolvedMediaItem | null => {
    if (!item || typeof item !== 'object') return null;

    const candidate = item as Record<string, unknown>;
    const pathValue = candidate.Path ?? candidate.path;
    const urlValue = candidate.Url ?? candidate.url;

    if (typeof pathValue !== 'string' || typeof urlValue !== 'string') return null;

    const path = pathValue.trim();
    const url = urlValue.trim();

    if (!path || !url) return null;

    return { path, url };
  };

  if (Array.isArray(unwrapped)) {
    return unwrapped.map(normalizeItem).filter((item): item is ResolvedMediaItem => item != null);
  }

  const singleItem = normalizeItem(unwrapped);
  return singleItem ? [singleItem] : [];
};
